import { useContext, useEffect, useMemo, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

import { FormInputsType } from '@pages/FormCreateOrEdit/types'
import { ArraySectionsType, FormEventType } from '@pages/WindowsCreateOrEdit/types'
import { WindowsCreateOrEditContext } from '@pages/WindowsCreateOrEdit/WindowsCreateOrEdit'

import { useFetchDictionariesByCategoryCodeQuery } from '@redux/api'

import {
  CATEGORIES,
  FIELD_BIND_TYPE_ENUM,
  GENERATOR_INPUT_TYPE,
  MAX_INPUT_LENGTH,
} from '@constants'

type UseEventEditProps = {
  sectionIndex: number
  tabIndex: number
  initialValues: FormEventType
  onSave: (values: FormEventType) => void
  onCancel: () => void
}

export const useEventEdit = ({
  sectionIndex,
  tabIndex,
  initialValues,
  onSave,
  onCancel,
}: UseEventEditProps) => {
  const { t } = useTranslation()
  const [showScriptValueDialogTitle, setShowScriptValueDialogTitle] = useState(false)

  const editEventForm = useForm<FormEventType>({
    defaultValues: initialValues,
  })

  const { watch: editEventFormWatch, setValue, handleSubmit, reset } = editEventForm

  const watchEditValueType = editEventFormWatch('valueType')
  const watchEditValue = editEventFormWatch('value')

  const { watch } = useFormContext<ArraySectionsType>()
  const watchTabObject = watch(`sections.${sectionIndex}.tabs.${tabIndex}.object`)

  const { windowVariables } = useContext(WindowsCreateOrEditContext)

  const { data: typesWindowEvents } = useFetchDictionariesByCategoryCodeQuery(
    CATEGORIES.BINDING_WINDOW_EVENTS_ACTION
  )

  const eventOptions = typesWindowEvents
    ? typesWindowEvents.map(event => ({
        label: event.title,
        id: event.code,
      }))
    : []

  const windowVariablesOptions = useMemo(
    () =>
      windowVariables.map(variable => ({
        label: variable.title,
        id: variable.id,
      })),
    [windowVariables]
  )

  useEffect(() => {
    reset(initialValues)
  }, [initialValues, reset])

  const handleSetShowScriptValueDialogTitle = (value: boolean) =>
    setShowScriptValueDialogTitle(value)

  const handleCloseScriptDialog = () => {
    setShowScriptValueDialogTitle(false)
  }

  const handleSaveScriptDialog = (value: string) => {
    setValue('value', value, { shouldDirty: true })
    setShowScriptValueDialogTitle(false)
  }

  const handleChangeEventType = () => {
    setValue('value', '')
    setValue('pathArray', null)
  }

  const handleConfirm = handleSubmit(onSave)

  const handleCancel = () => {
    onCancel()
  }

  const inputStatic: FormInputsType[] = [
    {
      name: 'value',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      label: i18next.t('window.event.value.label'),
      labelPlacement: 'top',
      formInputContainerSx: { mb: '0px' },
      labelSx: { width: '100%' },
      rules: {
        required: true,
      },
    },
  ]

  const inputJs: FormInputsType[] = [
    {
      name: 'value',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: t('window.event.value.javascript.placeholder'),
      maxLengthInput: MAX_INPUT_LENGTH,
      label: t('window.event.value.label'),
      labelPlacement: 'top',
      value: watchEditValue,
      readOnly: true,
      rules: {
        required: true,
        maxLength: MAX_INPUT_LENGTH,
      },
      formInputContainerSx: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        '& .MuiButton-root': {
          mt: 4,
        },
      },
      formLabelSx: {
        mr: 0.75,
      },
      additionalBtn: {
        isEnabled: true,
        text: 'edit',
        color: 'primary',
        variant: 'contained',
        onClick: () => handleSetShowScriptValueDialogTitle(true),
      },
    },
  ]

  const getInputValue = () => {
    switch (watchEditValueType?.id) {
      case FIELD_BIND_TYPE_ENUM.STATIC:
        return inputStatic
      case FIELD_BIND_TYPE_ENUM.FIELD:
        return []
      case FIELD_BIND_TYPE_ENUM.JS:
        return inputJs
      default:
        return inputStatic
    }
  }

  const inputValue = getInputValue()

  return {
    state: {
      editEventForm,
      showScriptValueDialogTitle,
    },
    handlers: {
      handleCloseScriptDialog,
      handleSaveScriptDialog,
      handleChangeEventType,
      handleConfirm,
      handleCancel,
    },
    data: {
      eventOptions,
      windowVariablesOptions,
      inputValue,
      watchEditValueType,
      watchEditValue,
      watchObjectTab: watchTabObject,
    },
  }
}
